// Клиентский кэш состояния промежуточных дел. Источник истины — бэк:
// сюда попадают только ответы API, снапшоты state_changed и секундные тики
// из /ws/intermediate. Не персистится — на старте наполняется GET-списком.
import { create } from 'zustand';

import type { IntermediateState, IntermediateTick } from './api';

interface IntermediateStoreState {
  items: IntermediateState[];
  /** Описания дел, созданных в этой сессии: бэк описание в состоянии трекинга
   *  не отдаёт, для остальных заголовок резолвится по списку задач. */
  descriptions: Record<number, string>;

  setAll: (items: IntermediateState[]) => void;
  /** Снапшот из ответа API или WS state_changed; completed — убирает из списка. */
  upsert: (state: IntermediateState) => void;
  applyTicks: (ticks: IntermediateTick[]) => void;
  setDescription: (taskId: number, text: string) => void;
  remove: (taskId: number) => void;
}

export const useIntermediateStore = create<IntermediateStoreState>()((set) => ({
  items: [],
  descriptions: {},

  setAll: (items) =>
    set({
      items: items.filter((it) => it.status !== 'completed'),
    }),

  upsert: (state) =>
    set((s) => {
      if (state.status === 'completed') {
        return { items: s.items.filter((it) => it.taskId !== state.taskId) };
      }
      const idx = s.items.findIndex((it) => it.taskId === state.taskId);
      if (idx === -1) {
        return { items: [...s.items, state] };
      }
      const items = s.items.slice();
      items[idx] = state;
      return { items };
    }),

  applyTicks: (ticks) =>
    set((s) => {
      if (!ticks.length) {
        return s;
      }
      const byId = new Map(ticks.map((t) => [t.taskId, t]));
      return {
        items: s.items.map((it) => {
          const tick = byId.get(it.taskId);
          return tick
            ? {
                ...it,
                elapsedSeconds: tick.elapsedSeconds,
                remainingSeconds: tick.remainingSeconds,
              }
            : it;
        }),
      };
    }),

  setDescription: (taskId, text) =>
    set((s) => ({
      descriptions: { ...s.descriptions, [taskId]: text },
    })),

  remove: (taskId) =>
    set((s) => ({
      items: s.items.filter((it) => it.taskId !== taskId),
    })),
}));
